// Client API for the SocialKit Composition (the editable layered document behind a
// post). The composition lives on the creative's generation snapshot server-side
// (CreativeGenerations.composition), fetched on demand when the editor opens.

import { OpenAPI } from '../../../openapi/core/OpenAPI'
import { request as openApiRequest } from '../../../openapi/core/request'
import type { Composition } from '../composition/types'

export type CompositionResponse = {
  // A static post is a PostComposition; a carousel is a CarouselComposition (deck of slides).
  composition: Composition | null
  spec: Record<string, unknown> | null
}

export type RenderedComposition = {
  imageUrl: string
  attachmentId: string
}

/** GET /v1/creatives/:id/composition — the editable doc + the revise spec. */
export function fetchComposition(creativeId: string): Promise<CompositionResponse> {
  return openApiRequest(OpenAPI, {
    method: 'GET',
    url: '/v1/creatives/{creativeId}/composition',
    path: { creativeId },
  })
}

/** PUT /v1/creatives/:id/composition — persist an edited doc (meta.dirty=true). */
export function saveComposition(
  creativeId: string,
  composition: Composition
): Promise<CompositionResponse> {
  return openApiRequest(OpenAPI, {
    method: 'PUT',
    url: '/v1/creatives/{creativeId}/composition',
    path: { creativeId },
    body: { composition },
    mediaType: 'application/json',
  })
}

/** POST /v1/creatives/:id/composition/render — render the edited doc to a master,
 *  re-host to GCS, and swap it onto the creative. Returns the new image. */
export function renderComposition(
  creativeId: string,
  composition?: Composition
): Promise<RenderedComposition> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/creatives/{creativeId}/composition/render',
    path: { creativeId },
    body: composition ? { composition } : {},
    mediaType: 'application/json',
  })
}

/** Explicit master render for a VIDEO composition. Unlike static/carousel, a video's autosave
 *  never triggers a render — the reel is expensive to re-composite (VO synth + clip stitch), so
 *  the render is ONLY driven by the user pressing "Render preview". This posts to the SAME
 *  :id/composition/render endpoint (the server branches on the persisted composition's shape);
 *  the client call is shape-agnostic. The caller re-fetches the composition + refreshes the
 *  content (the new master <video>) after this resolves, mirroring the carousel post-render flow. */
export function renderVideoComposition(creativeId: string): Promise<void> {
  return renderComposition(creativeId).then(() => undefined)
}

/** POST /v1/creatives/:id/bring-to-life — start/resume hydration of a blueprint reel: film every
 *  still-planned beat, then render + score the master. Charges the one video credit if not
 *  already charged. Async 202; progress arrives as videoHydration content-updated events. */
export function bringToLife(creativeId: string): Promise<{ status: 'hydrating' }> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/creatives/{creativeId}/bring-to-life',
    path: { creativeId },
  })
}

/** POST /v1/creatives/:id/synth-beat-vo — synthesize ONE beat's voiceover for the live preview
 *  (P1: Play speaks before any render). Sync (a few seconds); the server stores the mp3 durably,
 *  re-times captions from the word timings, and the next render reuses the exact take. Free. */
export function synthBeatVo(
  creativeId: string,
  beatId: string,
  voiceId?: string
): Promise<{
  beatId: string
  voSrc: { kind: 'attachment'; attachmentId: string }
  captions: Array<{ text: string; start: number; end: number }>
}> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/creatives/{creativeId}/synth-beat-vo',
    path: { creativeId },
    body: { beatId, ...(voiceId ? { voiceId } : {}) },
    mediaType: 'application/json',
  })
}

/** POST /v1/creatives/:id/regenerate-music — regenerate the reel's music bed, optionally steered
 *  by a prompt ("slow jazzy piano"). Async: returns { status:'generating' } (the track takes
 *  ~2-3 min); the caller polls `fetchComposition` until music.src changes. Free. */
export function regenerateReelMusic(
  creativeId: string,
  prompt?: string
): Promise<{ status: 'generating' }> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/creatives/{creativeId}/regenerate-music',
    path: { creativeId },
    body: prompt?.trim() ? { prompt: prompt.trim() } : {},
    mediaType: 'application/json',
  })
}

/** POST /v1/creatives/:id/generate-beat-clip — generate a new background clip for ONE reel beat
 *  (t2v from a scene prompt / i2v from an uploaded image). Async: returns { status:'generating' }
 *  immediately (clip gen is 1-2 min); the caller polls `fetchComposition` until the beat's
 *  background clipSrc changes, then applies it. */
export function generateBeatClip(
  creativeId: string,
  body: { beatId: string; shotKind: 't2v' | 'i2v'; sceneIdea?: string; imageAttachmentId?: string }
): Promise<{ status: 'generating' }> {
  return openApiRequest(OpenAPI, {
    method: 'POST',
    url: '/v1/creatives/{creativeId}/generate-beat-clip',
    path: { creativeId },
    body,
    mediaType: 'application/json',
  })
}
